import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDown, MapPin, Microscope } from "lucide-react";
import { MSG, wa } from "../../data/site";
import { WhatsAppIcon } from "./Icons";

const lines = {
    hidden: {},
    show: { transition: { staggerChildren: 0.14, delayChildren: 0.25 } },
};

const lineUp = {
    hidden: { y: "115%" },
    show: { y: "0%", transition: { duration: 0.95, ease: [0.22, 1, 0.36, 1] } },
};

const fade = (delay) => ({
    initial: { opacity: 0, y: 22 },
    animate: { opacity: 1, y: 0 },
    transition: { duration: 0.8, delay, ease: [0.22, 1, 0.36, 1] },
});

export const Hero = () => {
    const ref = useRef(null);
    const { scrollYProgress } = useScroll({
        target: ref,
        offset: ["start start", "end start"],
    });
    const imgY = useTransform(scrollYProgress, [0, 1], [0, 90]);
    const textY = useTransform(scrollYProgress, [0, 1], [0, 40]);

    return (
        <section
            id="inicio"
            ref={ref}
            data-testid="hero-section"
            className="hero-grid-pattern relative overflow-hidden bg-[#FAFCFF] pt-32 pb-20 md:pt-40 md:pb-28"
        >
            <div
                className="pointer-events-none absolute -top-40 right-[-10%] h-[34rem] w-[34rem] rounded-full bg-[#EFF5FF]"
                aria-hidden="true"
            />
            <div className="relative mx-auto grid max-w-7xl items-center gap-16 px-4 sm:px-6 lg:grid-cols-2 lg:gap-12 lg:px-8">
                <motion.div style={{ y: textY }}>
                    <motion.div {...fade(0.05)}>
                        <span
                            data-testid="hero-overline"
                            className="inline-flex items-center gap-3 text-xs font-bold uppercase tracking-[0.22em] text-[#1A6AFF]"
                        >
                            <span className="h-px w-8 bg-[#1A6AFF]" aria-hidden="true" />
                            Dr. Gabriel Riccio | Endodontista
                        </span>
                    </motion.div>

                    <motion.h1
                        variants={lines}
                        initial="hidden"
                        animate="show"
                        className="mt-6 text-4xl font-extrabold leading-[1.12] tracking-tight text-slate-900 sm:text-5xl lg:text-6xl"
                    >
                        <span className="block overflow-hidden pb-1">
                            <motion.span variants={lineUp} className="block">
                                Tratamento de canal
                            </motion.span>
                        </span>
                        <span className="block overflow-hidden pb-2">
                            <motion.span variants={lineUp} className="block">
                                com{" "}
                                <span className="relative inline-block text-[#1A6AFF]">
                                    microscopia
                                    <svg
                                        className="absolute -bottom-1 left-0 w-full"
                                        viewBox="0 0 220 12"
                                        fill="none"
                                        aria-hidden="true"
                                    >
                                        <path
                                            d="M3 9C60 3 160 3 217 8"
                                            stroke="#1A6AFF"
                                            strokeWidth="4"
                                            strokeLinecap="round"
                                            opacity="0.35"
                                        />
                                    </svg>
                                </span>
                            </motion.span>
                        </span>
                    </motion.h1>

                    <motion.p
                        {...fade(0.75)}
                        data-testid="hero-subtitle"
                        className="mt-6 max-w-xl text-lg leading-relaxed text-slate-700 md:text-xl"
                    >
                        Atendimento especializado com tecnologia avançada, precisão e cuidado
                        individualizado para preservar seus dentes naturais.
                    </motion.p>
                    <motion.p {...fade(0.9)} className="mt-4 max-w-xl text-base leading-relaxed text-slate-500">
                        Uma abordagem moderna da endodontia, com microscopia operatória e recursos
                        tecnológicos que auxiliam na realização dos tratamentos.
                    </motion.p>

                    <motion.div {...fade(1.05)} className="mt-9 flex flex-col gap-4 sm:flex-row sm:items-center">
                        <a
                            href={wa(MSG.canal)}
                            target="_blank"
                            rel="noopener noreferrer"
                            data-testid="hero-canal-whatsapp-button"
                            className="inline-flex items-center justify-center gap-2.5 whitespace-nowrap rounded-full bg-[#1A6AFF] px-7 py-4 text-sm font-semibold text-white shadow-xl shadow-blue-600/25 transition-all duration-300 hover:-translate-y-0.5 hover:bg-[#0052E0]"
                        >
                            <WhatsAppIcon className="h-5 w-5" />
                            Preciso de um tratamento de canal
                        </a>
                        <a
                            href="#sobre"
                            data-testid="hero-about-button"
                            className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-full border border-slate-300 bg-white/70 px-7 py-4 text-sm font-semibold text-slate-800 backdrop-blur transition-all duration-300 hover:-translate-y-0.5 hover:border-[#1A6AFF] hover:text-[#1A6AFF]"
                        >
                            Conheça o especialista
                            <ArrowDown className="h-4 w-4" />
                        </a>
                    </motion.div>
                </motion.div>

                <motion.div style={{ y: imgY }} className="relative mx-auto w-full max-w-md lg:max-w-none">
                    <motion.div
                        initial={{ clipPath: "inset(10% 10% 10% 10% round 2.5rem)", opacity: 0 }}
                        animate={{ clipPath: "inset(0% 0% 0% 0% round 2.5rem)", opacity: 1 }}
                        transition={{ duration: 1.15, delay: 0.35, ease: [0.22, 1, 0.36, 1] }}
                        className="relative"
                    >
                        <div
                            className="absolute -inset-3 -z-10 rounded-[2.8rem] border border-[#BFDBFE] bg-white/60"
                            aria-hidden="true"
                        />
                        <img
                            src="/images/hero-doctor.jpg"
                            alt="Dr. Gabriel Riccio, endodontista em Santos"
                            data-testid="hero-doctor-photo"
                            className="aspect-[15/16] w-full rounded-[2.5rem] bg-white object-cover"
                            fetchpriority="high"
                        />
                    </motion.div>

                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 1.1, duration: 0.7 }}
                        className="absolute -left-3 top-8 sm:-left-8"
                    >
                        <motion.div
                            animate={{ y: [0, -9, 0] }}
                            transition={{ duration: 5, repeat: Infinity, ease: "easeInOut" }}
                            className="flex items-center gap-3 rounded-2xl border border-slate-200/80 bg-white/90 px-4 py-3 shadow-lg shadow-slate-900/5 backdrop-blur"
                            data-testid="hero-chip-microscopia"
                        >
                            <span className="flex h-9 w-9 items-center justify-center rounded-xl bg-[#EFF5FF] text-[#1A6AFF]">
                                <Microscope className="h-5 w-5" />
                            </span>
                            <span className="text-sm font-semibold text-slate-800">
                                Microscopia operatória
                            </span>
                        </motion.div>
                    </motion.div>

                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 1.25, duration: 0.7 }}
                        className="absolute -right-3 bottom-10 sm:-right-6"
                    >
                        <motion.div
                            animate={{ y: [0, 9, 0] }}
                            transition={{ duration: 6, repeat: Infinity, ease: "easeInOut" }}
                            className="flex items-center gap-3 rounded-2xl border border-slate-200/80 bg-white/90 px-4 py-3 shadow-lg shadow-slate-900/5 backdrop-blur"
                            data-testid="hero-chip-localizacao"
                        >
                            <span className="flex h-9 w-9 items-center justify-center rounded-xl bg-[#EFF5FF] text-[#1A6AFF]">
                                <MapPin className="h-5 w-5" />
                            </span>
                            <span className="text-sm font-semibold text-slate-800">
                                Boqueirão, Santos — SP
                            </span>
                        </motion.div>
                    </motion.div>
                </motion.div>
            </div>
        </section>
    );
};
